import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteReleaseLyricsFileMutation, getTrackQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteReleaseLyricsFile = (trackId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteReleaseLyricsFileAction, { loading }] = useMutation(deleteReleaseLyricsFileMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getTrackQuery,
                variables: {
                    where: { id: trackId },
                },
            },
        ],
    });

    const { deleteReleaseLyricsFilePending: deleteReleaseLyricsFilePendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteReleaseLyricsFile = async (fileId) => {
        try {
            await deleteReleaseLyricsFileAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", {
                                entity: `${t("releases.entity")} ${t("labels.lyricsFile")}`,
                            }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id: fileId },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteReleaseLyricsFilePendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("labels.lyricsFile") }), {
                    toastId: `${deleteReleaseLyricsFilePendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteReleaseLyricsFile };
};

export default useDeleteReleaseLyricsFile;
